import { Vector3 } from "three";
import type { OBB } from "three/examples/jsm/math/OBB.js";
import type { CornerLineSnapDebug } from "./ObjectCollisionHits";

/** Uygulanan hiza. */
export const CORNER_SNAP_APPLY_M = 0.08;
/** Debug’da görünen adaylar. */
export const CORNER_SNAP_DEBUG_M = 0.25;
/** Engel aramak için broad şişirme. */
export const CORNER_SNAP_PROBE_M = 0.3;
/**
 * Yapışık yüzlerde along≈0 sahte adaylar gerçek hizayı ezer.
 * Bundan küçük fark zaten oturmuş sayılır; kazanan olamaz.
 */
const CORNER_SNAP_MIN_M = 0.002;

interface XzPoint {
  x: number;
  z: number;
}

export type CornerLineAxis = "right" | "forward";

export interface CornerLineSnapResult {
  translation: Vector3;
  snaps: CornerLineSnapDebug[];
}

function localXZ(obb: OBB): { right: Vector3; forward: Vector3 } | undefined {
  const e = obb.rotation.elements;
  const right = new Vector3(e[0], 0, e[2]);
  const forward = new Vector3(e[6], 0, e[8]);
  if (right.lengthSq() < 1e-8 || forward.lengthSq() < 1e-8) return undefined;
  right.normalize();
  forward.normalize();
  return { right, forward };
}

function xzCorners(obb: OBB, axes: { right: Vector3; forward: Vector3 }): XzPoint[] {
  const c = obb.center;
  const hx = obb.halfSize.x;
  const hz = obb.halfSize.z;
  const signs: Array<[number, number]> = [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ];
  return signs.map(([sx, sz]) => ({
    x: c.x + axes.right.x * hx * sx + axes.forward.x * hz * sz,
    z: c.z + axes.right.z * hx * sx + axes.forward.z * hz * sz,
  }));
}

/** Snap araması: çarpışmayan ama yakın OBB’ler de görülsün. */
export function inflateObbXZ(obb: OBB, padM: number): OBB {
  const out = obb.clone();
  out.halfSize.x += padM;
  out.halfSize.z += padM;
  return out;
}

/**
 * Sabit kutunun forward / right çizgisine köşe yakınsa hiza.
 * Debug 25 cm’ye kadar çizer; öteleme yalnız 8 cm içinde.
 */
export function cornerLineSnap(
  draggedObbs: readonly OBB[],
  staticObbs: readonly OBB[],
  y = 0,
): CornerLineSnapResult {
  const snaps: CornerLineSnapDebug[] = [];
  let bestAbs = CORNER_SNAP_APPLY_M;
  let bestIndex = -1;
  let bestShift = new Vector3();

  for (const fixed of staticObbs) {
    const fixedAxes = localXZ(fixed);
    if (!fixedAxes) continue;
    const fixedCorners = xzCorners(fixed, fixedAxes);
    const lines: Array<{ axis: CornerLineAxis; dir: Vector3 }> = [
      { axis: "right", dir: fixedAxes.right },
      { axis: "forward", dir: fixedAxes.forward },
    ];

    for (const dragged of draggedObbs) {
      const dragAxes = localXZ(dragged);
      if (!dragAxes) continue;
      const dragCorners = xzCorners(dragged, dragAxes);
      const cornerY = dragged.center.y;

      for (const line of lines) {
        for (const s of fixedCorners) {
          for (const d of dragCorners) {
            const along =
              (d.x - s.x) * line.dir.x + (d.z - s.z) * line.dir.z;
            const abs = Math.abs(along);
            // Oturmuş (~0) çiftler seçimi kilitlemesin; yalnız gerçek kayma.
            if (abs < CORNER_SNAP_MIN_M || abs >= CORNER_SNAP_DEBUG_M) continue;
            const from = new Vector3(d.x, cornerY || y, d.z);
            const to = from.clone().addScaledVector(line.dir, -along);
            const index = snaps.length;
            snaps.push({
              from,
              to,
              chosen: false,
              axis: line.axis,
            });
            if (abs < bestAbs) {
              bestAbs = abs;
              bestIndex = index;
              bestShift.copy(line.dir).multiplyScalar(-along);
            }
          }
        }
      }
    }
  }

  if (bestIndex >= 0) snaps[bestIndex].chosen = true;
  return { translation: bestShift, snaps };
}

export function cornerLineSnapDelta(
  draggedObbs: readonly OBB[],
  staticObbs: readonly OBB[],
): Vector3 {
  return cornerLineSnap(draggedObbs, staticObbs).translation;
}
